import {
    drawMeridians,
    drawParallels,
    makeGridParallels,
    makeGridMeridians
} from './UTM';
import {makeSources} from './GridUtils';
import {toMGRS} from './MGRS';
import intersect from '@turf/line-intersect';
import {forward} from 'mgrs';

const coordinateGridSpacing = [
    100000, // 0
    100000, // 1
    100000, // 2
    100000, // 3
    100000, // 4
    100000, // 5
    100000, // 6
    100000, // 7
    50000, // 8
    10000, // 9
    10000,  // 10
    10000,  // 11
    1000,  // 12
    1000,  // 13
    1000,   // 14
    1000,   // 15
    100,   // 16
    100,    // 17
    100,    // 18
    100,    // 19
    100,    // 20
];

// Used for creating labels with proper accuracy
const zoomPrecision = [
    4, // 0
    4, // 1
    4, // 2
    4, // 3
    4, // 4
    4, // 5
    4, // 6
    4, // 7
    1, // 8
    1, // 9
    1, // 10
    1, // 11
    2, // 12
    2, // 13
    2, // 14
    2, // 15
    3, // 16
    3, // 17
    3, // 18
    3, // 19
    3, // 20
];

function makeMGSRLayers(): mapboxgl.Layer[] {
    return [{
        id: 'graticule_line',
        type: 'line',
        source: 'graticule',
        paint: {
            'line-width': 1,
            'line-color': '#3C8CC8',
            'line-opacity': [
                'interpolate', ['linear'], ['zoom'],
                0, 0.1,
                12, 1,
            ],
        },
    }, {
        id: 'zone_label',
        type: 'symbol',
        source: 'graticule',
        filter: ['==', '$type', 'Point'],
        layout: {
            'text-field': ['get', 'label'],
            'text-anchor': 'center',
            'text-size': [
                'interpolate', ['linear'], ['zoom'],
                0, 7,
                14, 12
            ],
            'text-letter-spacing': 0.15,
            'text-font': ['Regular'],
        },
        paint: {
            'text-color': '#0C73AC',
        }
    }];
}

function makeGrid(
    meridians: GeoJSON.Feature<GeoJSON.LineString>[],
    parallels: GeoJSON.Feature<GeoJSON.LineString>[],
    zoom: number
): GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties>[] {
    const output = [];

    for (let i = 0; i < meridians.length - 1; i++) {
        for (let j = 0; j < parallels.length - 1; j++) {
            const intersection1 = intersect(meridians[i], parallels[j]).features[0];
            const intersection2 = intersect(meridians[i + 1], parallels[j]).features[0];
            const intersection3 = intersect(meridians[i], parallels[j + 1]).features[0];
            const intersection4 = intersect(meridians[i + 1], parallels[j + 1]).features[0];

            if (intersection1 && intersection2 && intersection3 && intersection4) {
                const coordinates = [
                    intersection1.geometry.coordinates[0] +
                    ((intersection2.geometry.coordinates[0] -
                      intersection1.geometry.coordinates[0]) /
                      2),
                    intersection3.geometry.coordinates[1] +
                    ((intersection1.geometry.coordinates[1] -
                      intersection3.geometry.coordinates[1]) /
                      2)
                ];
                const label = zoom < 8 ?
                    toMGRS(coordinates, zoomPrecision[zoom]) :
                    forward(coordinates, zoomPrecision[zoom]);

                const f: GeoJSON.Feature<GeoJSON.Point, GeoJSON.GeoJsonProperties> = {
                    type: 'Feature',
                    properties: {
                        label,
                    },
                    geometry: {
                        type: 'Point',
                        coordinates,
                    }
                };
                output.push(f);
            }
        }
    }
    return output;
}

function update(bounds: mapboxgl.LngLatBounds, zoom: number) {
    zoom = Math.floor(zoom);
    const interval = coordinateGridSpacing[zoom] || coordinateGridSpacing[18];

    const parallels = drawParallels(bounds);
    const meridians = drawMeridians(bounds);

    const features = [...meridians, ...parallels];

    const gridParallels = (zoom >= 6) ? makeGridParallels(bounds, features, interval) : [];
    const gridMeridians = (zoom >= 6) ? makeGridMeridians(bounds, interval) : [];

    const orderedParallels = [...parallels, ...gridParallels].sort((a, b) => {
        return a.geometry.coordinates[0][0] - b.geometry.coordinates[0][0];
    });

    let orderedMeridians = [...meridians, ...gridMeridians];
    orderedMeridians = orderedMeridians.sort((a, b) => {
        return a.geometry.coordinates[0][0] - b.geometry.coordinates[0][0];
    });

    const grid = makeGrid(orderedMeridians, orderedParallels, zoom);

    return {
        version: 8,
        glyphs: 'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf',
        metadata: {
            uniqueTilecacheKey: 'mgrs'
        },
        sources: makeSources([...orderedParallels, ...orderedMeridians, ...grid]),
        layers: makeMGSRLayers(),
    };
}

const MGRSGrid: GridGenerator = function (
    bounds: mapboxgl.LngLatBounds,
    zoom: number
): mapboxgl.Style {
    return update(bounds, zoom);
};

export default MGRSGrid;
